import React from "react";
import styles from "./styles.module.scss";
// form library
import { Form, Field } from "react-final-form";
// components
import CTAButton from "../../Components/CTAButton";
import FormError from "../../Components/FormError";
// utils
import { hasWhiteSpace } from "@intelligentlilli/lilli-utils";
import { isValidName, isValidEmail } from "../../Services/Utils";
// types
import { ServiceUser } from "../../Services/Hooks/useAddNewStaff";

type AddFriendOrFamilyFormType = {
  onSubmit: (
    forename: string,
    surname: string,
    email: string,
    suToAssociate: ServiceUser[],
  ) => void;
  createStaffEmailError: string | null;
  serviceUsers: any[];
  isLoading: boolean;
  onTrack: (eventProperties: Record<string, string>) => void;
}

const AddFriendOrFamilyForm = ({
  onSubmit,
  createStaffEmailError,
  serviceUsers,
  isLoading,
  onTrack
}: AddFriendOrFamilyFormType) => {
  
  const subtitle = `Fill in this form to invite a family member (or friend) to monitor this Lilli user. 
  Once added, they’ll be able to check in and view this Lilli user’s data through the Lilli mobile app. 
  Lilli’s app is available for both Android and iOS (Apple) devices. 
  After you click "Submit", we will email them instructions for how to download the app and login.`
 
  const openGDPR = () => {
    const baseUrl = window.location.origin;
    const newTabUrl = `${baseUrl}/invite-family-gdpr`;
    window.open(newTabUrl, '_blank');
    onTrack({
        action: "visited_GDPR"
    });
  };

  return (
    <Form
      onSubmit={(values) => {
        onSubmit(values.forename, values.surname, values.email.trim(), serviceUsers);
      }}
      validate={(values) => {
        type Errors = {  forename?: string; surname?: string; email?: string;};
        const errors: Errors = {};
        if (!values.forename) {
          errors.forename = "Required";
        }
        if (values.forename && !isValidName(values.forename)) {
          errors.forename = "Invalid forename";
        }
        if (!values.surname) {
          errors.surname = "Required";
        }
        if (values.surname && !isValidName(values.surname)) {
          errors.surname = "Invalid surname";
        }
        if (!values.email) {
          errors.email = "Required";
        }
        if (values.email && !isValidEmail(values.email.trim())) {
          errors.email = "Invalid email";
        }
        if (values.email && hasWhiteSpace(values?.email)) {
          errors.email = "Please remove whitespace";
        }
        return errors;
      }}
      render={({ handleSubmit, submitting, pristine, valid, form }) => (
        <form
          className={styles.form}
          onSubmit={(event) => {
            handleSubmit(event);
          }}
        >
          <div className={styles.form_header}>
            {subtitle}
          </div>
          <div className={styles.form_fields}>
            <Field name="forename">
              {({ input, meta }) => (
                <div className={styles.field}>
                  <div className={styles.label}>Family (or friend) first name</div>
                  <input className={styles.input} {...input} type="text" />
                  {meta.error && meta.touched && (
                    <span className={styles.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
            <Field name="surname">
              {({ input, meta }) => (
                <div className={styles.field}>
                  <div className={styles.label}>Family (or friend) last name</div>
                  <input className={styles.input} {...input} type="text" />
                  {meta.error && meta.touched && (
                    <span className={styles.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
            <Field name="email">
              {({ input, meta }) => (
                <div className={styles.field}>
                  <div className={styles.label}>Family (or friend) email</div>
                  <input className={styles.input} {...input} type="text" />
                  {meta.error && meta.touched && (
                    <span className={styles.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
          </div>
          {createStaffEmailError && <FormError error={createStaffEmailError} />}
          <CTAButton
            type="submit"
            disabled={isLoading || submitting || pristine || !valid}
            className={styles.submit}
          >
            {isLoading
              ? `Submitting...`
              : "Submit"}
          </CTAButton>
          <div className={styles.form_footer}>
            By clicking Submit you agree to our{" "}
              <span className={styles.footer_span} onClick={openGDPR}>
                GDPR and sharing policies
              </span>
          </div>
        </form>
      )}
    />
  );
};

export default AddFriendOrFamilyForm;
